import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import { checkInBooking, seatBooking, checkOutBooking, makeSaleReference, SETTLEMENT_CUTOFF_HOUR } from '@/domain/sessionFlow';
import { projectedRecommendation, recommendPaymentMethod } from '@/domain/cashFlow';
import SteeringExplainer from '@/components/floor/SteeringExplainer';
import { canRecordRebookOutcome } from '@/domain/permissions';
import { sendWaiver } from '@/domain/waiverFlow';
import { bookingPriceAud } from '@/domain/pricing';
import { aud, customerName, simTimeFull } from '@/utils/format';
import { depositPaidAud as depositPaidFromDeposit } from '@/utils/bookingSummary';
import { pickReferences } from '@/data/heroRecords';
import { inspirationFor } from '@/utils/imageHelpers';
import PhotoGallery from '@/components/PhotoGallery';
import RebookOutcomePanel from '@/components/RebookOutcomePanel';
import InfoTip from '@/components/InfoTip';
import WaiverModal from '@/components/WaiverModal';
import CustomerFlagChips from '@/components/CustomerFlagChips';
import { waiverTone } from '@/utils/statusTone';
import { useMakeSaleDraftStore, type Step, type SaleState } from '@/state/makeSaleDraftStore';
import { rebookMinIso } from '@/utils/rebookWindow';
import type { MerchItem, RebookAttribution, RebookOutcome } from '@/domain/types';
import {
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Radio,
  Sparkles,
  Wallet,
  CheckCircle2,
  ClipboardList,
  CircleDot,
  CreditCard,
  Camera,
  ArrowRight,
  ShieldCheck,
  Armchair,
  AlertTriangle,
  Send,
  Plus,
  Search,
  X,
  Banknote,
  Info,
} from 'lucide-react';
import clsx from 'clsx';

/**
 * The linear make-sale step machine (`Step`) and the payment detail
 * (`SaleState`) live in `makeSaleDraftStore` so an in-progress make-sale can be
 * snapshotted for the custom-date round-trip. Notes + photo snaps are ambient
 * panels, addable at any step; checkout stays linear: Progress -> Rebook ->
 * Payment.
 */
function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s
    .toString()
    .padStart(2, '0')}`;
}

export default function FloorBooking() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const booking = useBrainStore((s) => (bookingId ? s.bookings[bookingId] : undefined));
  const customer = useBrainStore((s) =>
    booking ? s.customers[booking.customerId] : undefined,
  );
  const artist = useBrainStore((s) => (booking ? s.artists[booking.artistId] : undefined));
  const piece = useBrainStore((s) =>
    booking?.pieceId ? s.pieces[booking.pieceId] : undefined,
  );
  const deposit = useBrainStore((s) =>
    booking?.depositId ? s.deposits[booking.depositId] : undefined,
  );
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const addPieceProgressPhoto = useBrainStore((s) => s.addPieceProgressPhoto);
  // Recording the rebook outcome is a studio-manager job at the make-sale.
  // Artists/apprentices only leave the lightweight in-chair read.
  const persona = useSessionStore((s) => s.persona);
  const canRebook = persona ? canRecordRebookOutcome(persona) : false;

  // Restore any in-progress make-sale draft saved before a custom-date detour to
  // the calendar (see onPickCustomDate). Read once, synchronously, so the state
  // initialisers below can seed from it.
  const draft0 = bookingId ? useMakeSaleDraftStore.getState().drafts[bookingId] : undefined;
  const [restoredFromDraft] = useState(() => !!draft0);

  // Step derives from booking status by default, but the wizard can advance
  // state-locally. A restored draft lands straight back on the rebook step.
  const [step, setStep] = useState<Step>(draft0?.step ?? 'overview');
  // Fast-forwards past steps already done (e.g. a deep link straight to an
  // in-progress booking) the first time real booking data is available. The
  // ref is armed on that FIRST data-bearing render regardless of the current
  // step - a resumed-from-draft session restores at a non-overview step (the
  // custom-date detour restores at 'rebook-outcome'), and if arming were
  // conditioned on `step === 'overview'` the guard would never latch, leaving
  // a later Back-to-overview (D4) free to re-trigger the jump and silently
  // undo the click. Arming first, then fast-forwarding only from 'overview',
  // means Back only ever moves the local wizard step and never rewinds the
  // booking's own status. `booking` starts undefined until the brain store's
  // async fixture load resolves, so the `!booking` guard defers arming past
  // that first, data-less render rather than wasting the one-time sync on it.
  const didSyncStepFromStatusRef = useRef(false);
  useEffect(() => {
    if (didSyncStepFromStatusRef.current) return;
    if (!booking) return;
    didSyncStepFromStatusRef.current = true;
    if (step !== 'overview') return;
    if (booking.status === 'in-chair') setStep('in-chair');
    else if (booking.status === 'checked-in') setStep('checked-in');
  }, [booking, step]);
  const [sessionNote, setSessionNote] = useState(draft0?.sessionNote ?? '');
  const [futurePhrase, setFuturePhrase] = useState(draft0?.futurePhrase ?? '');
  const [hoursToAdd, setHoursToAdd] = useState(draft0?.hoursToAdd ?? 0);
  const [markComplete, setMarkComplete] = useState(draft0?.markComplete ?? false);
  // Ambient panels are collapsed by default — the operator opens them when
  // they need to jot something or snap a photo. The snap nudge inside the
  // Progress step can pop the snaps panel open programmatically.
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [snapsExpanded, setSnapsExpanded] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'cash' | 'mixed'>(
    draft0?.paymentMethod ?? 'card',
  );
  // Make-sale detail captured on the payment step. `depositApplied === null`
  // means "use the default for the rebook outcome" (roll -> 0, apply -> full deposit).
  const [sale, setSale] = useState<SaleState>(
    draft0?.sale ?? {
      depositApplied: null,
      depositReason: '',
      merchLines: [],
      cashTendered: 0,
      adjustment: null,
    },
  );
  const merchItems = useBrainStore((s) => s.merchItems);
  const [paymentDone, setPaymentDone] = useState(false);
  const [rolledDepositId, setRolledDepositId] = useState<string | undefined>();
  const [snappedPhotos, setSnappedPhotos] = useState<string[]>(draft0?.snappedPhotos ?? []);
  // A custom rebook slot picked on the calendar and carried back via the draft.
  const [customSlot] = useState<{ iso: string } | undefined>(
    draft0?.customSlotIso ? { iso: draft0.customSlotIso } : undefined,
  );
  // Captured at the rebook step and consumed by the payment step +
  // checkOutBooking at the payment step's continue.
  const [rebookCapture, setRebookCapture] = useState<{
    outcome: RebookOutcome;
    attribution: RebookAttribution;
    selectedSlotIso?: string;
    // L3: piece progress captured on a decline path (finished / partway + left).
    pieceState?: { finished: boolean; sessionsLeft?: number };
  } | null>(null);

  const sessionPrice = booking ? bookingPriceAud(booking, artist) : 0;
  // Ledger-aware: reflects what is ACTUALLY paid (partial deposits show the real
  // amount in), matching the deposit ledger the booking detail card reads.
  const depositPaidAud = depositPaidFromDeposit(deposit);
  const depositRef = deposit?.depositReference;
  const rebookKind = rebookCapture?.outcome.kind;
  // Both rebook wins roll the deposit forward: locking a slot now, or holding it
  // for a later booking.
  const rolloverDeposit = rebookKind === 'rebookedInChair' || rebookKind === 'willRebookLater';
  // Deposit applied today: default from the rebook outcome (roll → 0, else the
  // full deposit), overridable to a partial amount on the payment step.
  const depositAppliedEffective = sale.depositApplied ?? (rolloverDeposit ? 0 : depositPaidAud);
  const balanceDueAud = Math.max(0, sessionPrice - depositAppliedEffective);
  const merchTotalAud = sale.merchLines.reduce((sum, l) => sum + l.priceAud, 0);
  // Reasoned +/- charge adjustment (D3), signed - folded straight into collect now.
  const adjustmentAud = sale.adjustment?.amountAud ?? 0;
  const collectNowAud = balanceDueAud + merchTotalAud + adjustmentAud;
  // A clean rollover (deposit untouched today) drives the roll-forward logic; if
  // the manager applied any of the pot, it is no longer a clean rollover.
  const cleanRollover = rolloverDeposit && depositAppliedEffective === 0;
  const merchCatalogue = useMemo<MerchItem[]>(
    () =>
      Object.values(merchItems).filter(
        (m) => m.studioId === booking?.studioId && m.status === 'active',
      ),
    [merchItems, booking?.studioId],
  );

  // Live timer for in-chair state.
  const [, tick] = useState(0);
  useEffect(() => {
    if (step !== 'in-chair') return;
    const id = setInterval(() => tick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [step]);

  const elapsedMs = useMemo(() => {
    // Time in the chair runs from when they were seated (falls back to check-in
    // for older records seeded before seatedAt existed).
    const startedIso = booking?.seatedAt ?? booking?.checkedInAt;
    if (!startedIso) return 0;
    // Use the sim clock as the wall clock so seeded timestamps display sensibly.
    return Math.max(0, new Date(clockIso).getTime() - new Date(startedIso).getTime());
  }, [booking?.seatedAt, booking?.checkedInAt, clockIso]);

  useEffect(() => {
    // Don't clobber a restored draft's hours with the scheduled default.
    if (restoredFromDraft) return;
    if (booking?.durationMin) setHoursToAdd(booking.durationMin / 60);
  }, [booking?.durationMin, restoredFromDraft]);

  const initialised = useBrainStore((s) => s.initialised);
  if (!booking || !customer || !artist) {
    if (!initialised) {
      return (
        <section className="px-10 py-12" data-testid="floor-booking-loading">
          <button onClick={() => navigate('/floor')} className="btn-quiet text-[11px]" data-testid="floor-back">
            <ChevronLeft size={12} /> Back to floor
          </button>
          <p className="font-serif text-cream-soft mt-4">Loading booking…</p>
        </section>
      );
    }
    return (
      <section className="px-10 py-12">
        <button onClick={() => navigate('/floor')} className="btn-quiet text-[11px]" data-testid="floor-back">
          <ChevronLeft size={12} /> Back to floor
        </button>
        <p className="font-serif text-cream-soft mt-4">
          This booking isn&apos;t loaded. Pick another from the floor.
        </p>
      </section>
    );
  }

  // Step 1. The waiver gate lives in the domain function — if it refuses we stay
  // on the check-in step (the panel surfaces the "sign / resend" remedies).
  const onCheckIn = () => {
    const result = checkInBooking(booking.id);
    if (!result.ok) {
      if (result.reason === 'waiver-unsigned') {
        pushNotification({
          intensity: 'high',
          title: 'Waiver not signed',
          body: `${customerName(customer)} can't be checked in until the medical waiver is signed.`,
          ttlMs: 4000,
          customerId: customer.id,
        });
      }
      return;
    }
    pushNotification({
      intensity: 'high',
      title: 'Checked in',
      body: `${customerName(customer)} is checked in — seat them when a chair's ready.`,
      ttlMs: 4000,
    });
    setStep('checked-in');
  };

  // Step 2. Places the client in the artist's chair; only valid from checked-in.
  // The chair follows the artist, so there's no booth to pick — one action.
  const onSeat = () => {
    const result = seatBooking(booking.id);
    if (!result.ok) {
      pushNotification({
        intensity: 'high',
        title: 'Could not seat',
        body: `${customerName(customer)} needs to be checked in first.`,
        ttlMs: 4000,
      });
      return;
    }
    pushNotification({
      intensity: 'high',
      title: 'Placed in chair · recording armed',
      body: `${customerName(customer)} is in the chair with ${artist.firstName}.`,
      ttlMs: 4000,
    });
    setStep('in-chair');
  };

  const onResendWaiver = () => {
    sendWaiver({ bookingId: booking.id, resend: true });
    pushNotification({
      intensity: 'info',
      title: 'Waiver resent',
      body: `Medical waiver link resent to ${customer.firstName}. Watch the conversation for the signature.`,
      ttlMs: 4000,
      customerId: customer.id,
    });
  };

  // Wrap-up jumps straight into the checkout transaction. Notes + photos are
  // already addable via the ambient panels — nothing forces them as wizard
  // steps any more.
  const onWrapUp = () => setStep('progress');

  const onFinalisePayment = () => {
    setPaymentDone(true);
  };

  // Custom-date detour: snapshot the in-progress make-sale, then open the
  // calendar in rebook-pick mode. The calendar writes the chosen slot back onto
  // this draft and returns here, where the initialisers above restore it.
  const onPickCustomDate = () => {
    useMakeSaleDraftStore.getState().saveDraft(booking.id, {
      step: 'rebook-outcome',
      outcome: 'rebookedInChair',
      sale,
      sessionNote,
      futurePhrase,
      snappedPhotos,
      hoursToAdd,
      markComplete,
      paymentMethod,
      customSlotIso: customSlot?.iso,
    });
    navigate(`/calendar/${booking.studioId}`, {
      state: {
        rebookForBookingId: booking.id,
        rebookArtistId: booking.artistId,
        rebookMinDateIso: rebookMinIso(booking.start, clockIso),
      },
    });
  };

  const onCompleteCheckout = () => {
    if (piece && snappedPhotos.length > 0) {
      for (const url of snappedPhotos) {
        addPieceProgressPhoto(piece.id, {
          url,
          capturedAt: clockIso,
          bookingId: booking.id,
          caption: `Captured ${simTimeFull(clockIso)}`,
        });
      }
    }
    const result = checkOutBooking({
      bookingId: booking.id,
      sessionNote: sessionNote || undefined,
      futureReadinessPhrase: futurePhrase || undefined,
      hoursToAdd,
      // L3: the rebook panel's "Finished" choice also completes the piece; a
      // "Partway · N left" choice feeds the living estimatedTotalSessions.
      markPieceComplete: markComplete || rebookCapture?.pieceState?.finished === true,
      pieceSessionsLeft:
        rebookCapture?.pieceState && rebookCapture.pieceState.finished === false
          ? rebookCapture.pieceState.sessionsLeft
          : undefined,
      // The payment method is how TODAY'S balance was collected (cash/card/mixed).
      // Rolling the deposit forward is an independent fact carried by
      // `rolloverDepositToNextBooking` below — it must NOT overwrite the method,
      // or a cash balance collected alongside a roll gets recorded as
      // 'rolled-deposit' and dropped from the till (cashInAud = 0).
      paymentMethod,
      amountPaidAud: balanceDueAud,
      rolloverDepositToNextBooking: cleanRollover,
      depositAppliedAud: depositAppliedEffective,
      merchLines: sale.merchLines,
      cashTenderedAud: sale.cashTendered,
      adjustmentAud: sale.adjustment?.amountAud ?? 0,
      adjustmentReason: sale.adjustment?.reason,
      rebookCapture: rebookCapture ?? undefined,
    });
    setRolledDepositId(result.rolledDepositId);
    // Checkout is done — drop any custom-date draft we were holding.
    useMakeSaleDraftStore.getState().clearDraft(booking.id);
    if (rolloverDeposit && result.newBookingId && rebookCapture?.selectedSlotIso) {
      // The new booking is already created in checkOutBooking — take the agent
      // to the calendar focused on that day so they can verify the placement.
      navigate(`/calendar/${booking.studioId}`, {
        state: {
          focusDayIso: rebookCapture.selectedSlotIso,
          focusArtistId: booking.artistId,
        },
      });
    } else {
      setStep('done');
    }
  };

  const stepOrder: Step[] = [
    'overview',
    'checked-in',
    'in-chair',
    'progress',
    'rebook-outcome',
    'payment',
    'done',
  ];
  const stepIndex = stepOrder.indexOf(step);

  // Compact rebook-outcome summary shown on the payment step (WS2): the next
  // session's date when rebooked, otherwise why they didn't rebook.
  const rebookOutcome = rebookCapture?.outcome;
  const rebookSlotIso = rebookCapture?.selectedSlotIso ?? customSlot?.iso;
  const rebookSummary: { title: string; detail?: string } =
    rebookOutcome?.kind === 'rebookedInChair'
      ? {
          title: rebookSlotIso ? `Next session · ${simTimeFull(rebookSlotIso)}` : 'Next session locked in',
          detail: rolloverDeposit ? 'Deposit rolled forward' : undefined,
        }
      : rebookOutcome?.kind === 'willRebookLater'
        ? { title: 'Rebooking later', detail: 'Deposit held for the next booking' }
        : rebookOutcome?.kind === 'noRebookDepositApplied'
          ? { title: 'No rebook', detail: `Reason: ${rebookOutcome.reason.replace(/-/g, ' ')}` }
          : rebookOutcome?.kind === 'rebookDeferredToSales'
            ? { title: 'Handed to sales', detail: 'Sales team to follow up' }
            : { title: 'No rebook recorded' };

  return (
    <section className="px-8 py-8" data-testid={`floor-booking-${booking.id}`}>
      <button
        onClick={() => {
          useMakeSaleDraftStore.getState().clearDraft(booking.id);
          navigate('/floor');
        }}
        className="btn-quiet text-[11px] mb-4"
        data-testid="floor-back"
      >
        <ChevronLeft size={12} /> Back to floor
      </button>

      <header className="grid grid-cols-12 gap-6 mb-6">
        <div className="col-span-12 lg:col-span-8">
          <p className="label-small-caps text-gold-bright">{booking.studioId} · {artist.displayName}</p>
          <h1 className="heading-display text-3xl text-cream mt-2">{customerName(customer)}</h1>
          <p className="font-inter text-[11px] text-cream-soft mt-1 uppercase tracking-widest">
            {simTimeFull(booking.start)} · {booking.sessionLength.replace('-', ' ')} · {aud(sessionPrice)}
          </p>
          {piece ? (
            <p className="font-serif text-cream-soft mt-2">
              <span className="text-gold-bright font-inter">
                Session {(piece.sessionsCompleted ?? 0) + (booking.status === 'in-chair' ? 1 : booking.status === 'completed' ? 0 : 1)} of {piece.estimatedTotalSessions ?? '?'}
              </span>{' '}
              · {piece.title || piece.designType.replace('-', ' ')} on {piece.placement}
            </p>
          ) : null}
        </div>
        <aside className="col-span-12 lg:col-span-4 panel-dark p-4">
          <p className="label-small-caps text-gold-bright">Status</p>
          <p className="font-cinzel text-2xl text-cream mt-2 capitalize">
            {booking.status.replace('-', ' ')}
          </p>
          {booking.boothNumber ? (
            <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
              Booth {booking.boothNumber}
            </p>
          ) : null}
          {step === 'in-chair' ? (
            <div
              className="mt-3 flex items-center gap-2 text-blood font-mono text-sm"
              data-testid="in-chair-timer"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-blood animate-ping opacity-60" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-blood" />
              </span>
              REC · {formatElapsed(elapsedMs)}
            </div>
          ) : null}
        </aside>
      </header>

      <StepIndicator step={step} stepIndex={stepIndex} stepOrder={stepOrder} />

      <div className="grid grid-cols-12 gap-6 mt-6">
        <section className={clsx('col-span-12 space-y-4', step !== 'payment' && 'lg:col-span-8')}>
          {/* Ambient panels — always accessible. They sit above the current
              step so the operator can drop a note or snap a photo at any
              point in the flow without losing place. */}
          {step !== 'overview' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <NotesAmbientPanel
                expanded={notesExpanded}
                setExpanded={setNotesExpanded}
                sessionNote={sessionNote}
                setSessionNote={setSessionNote}
                futurePhrase={futurePhrase}
                setFuturePhrase={setFuturePhrase}
              />
              <SnapsAmbientPanel
                expanded={snapsExpanded}
                setExpanded={setSnapsExpanded}
                piece={piece}
                booking={booking}
                snappedPhotos={snappedPhotos}
                setSnappedPhotos={setSnappedPhotos}
              />
            </div>
          ) : null}

          {step === 'overview' ? (
            <CheckInPanel
              customer={customer}
              booking={booking}
              artist={artist}
              piece={piece}
              clockIso={clockIso}
              onCheckIn={onCheckIn}
              onResendWaiver={onResendWaiver}
            />
          ) : null}

          {step === 'checked-in' ? (
            <SeatPanel
              customer={customer}
              booking={booking}
              artist={artist}
              onSeat={onSeat}
              onBack={() => setStep('overview')}
            />
          ) : null}

          {step === 'in-chair' ? (
            <InChairPanel
              booking={booking}
              customer={customer}
              artist={artist}
              piece={piece}
              onWrapUp={onWrapUp}
              onBack={() => setStep('checked-in')}
            />
          ) : null}

          {step === 'progress' ? (
            <ProgressPanel
              piece={piece}
              hoursToAdd={hoursToAdd}
              setHoursToAdd={setHoursToAdd}
              markComplete={markComplete}
              setMarkComplete={setMarkComplete}
              defaultDurationHours={booking.durationMin / 60}
              snappedPhotosCount={snappedPhotos.length}
              existingProgressCount={(piece?.progressPhotos?.length ?? 0)}
              onJumpToSnaps={() => {
                setSnapsExpanded(true);
                requestAnimationFrame(() => {
                  document
                    .querySelector('[data-testid="ambient-snaps"]')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                });
              }}
              onContinue={() => setStep('rebook-outcome')}
              onBack={() => setStep('in-chair')}
            />
          ) : null}

          {step === 'rebook-outcome' ? (
            canRebook ? (
              <RebookOutcomePanel
                booking={booking}
                customer={customer}
                artist={artist}
                deposit={deposit}
                sessionPrice={sessionPrice}
                onPickCustomDate={onPickCustomDate}
                initialOutcome={customSlot ? 'rebookedInChair' : undefined}
                customSlot={customSlot}
                onBack={() => setStep('progress')}
                onContinue={(capture) => {
                  setRebookCapture(capture);
                  setStep('payment');
                }}
              />
            ) : (
              <RebookOutcomeBlockedPanel
                artistName={artist.firstName}
                onBack={() => setStep('progress')}
              />
            )
          ) : null}

          {step === 'payment' ? (
            <PaymentPanel
              booking={booking}
              customer={customer}
              piece={piece}
              artistName={artist.displayName}
              rebookSummary={rebookSummary}
              clockIso={clockIso}
              sessionPrice={sessionPrice}
              depositPaidAud={depositPaidAud}
              depositRef={depositRef}
              rolloverDeposit={rolloverDeposit}
              depositAppliedEffective={depositAppliedEffective}
              balanceDueAud={balanceDueAud}
              collectNowAud={collectNowAud}
              merchCatalogue={merchCatalogue}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              sale={sale}
              setSale={setSale}
              paymentDone={paymentDone}
              onCharge={onFinalisePayment}
              onContinue={onCompleteCheckout}
              onBack={() => setStep('rebook-outcome')}
            />
          ) : null}

          {step === 'done' ? (
            <DonePanel
              customerName={customerName(customer)}
              amount={collectNowAud}
              rolled={!!rolledDepositId}
              onBack={() => navigate('/floor')}
            />
          ) : null}
        </section>

        {/* On the payment step the customer context is folded into the panel's
            right rail, so the standalone sidebar is only shown on other steps. */}
        {step !== 'payment' ? (
          <aside className="col-span-12 lg:col-span-4 space-y-4">
            <CustomerSidebar customer={customer} piece={piece} />
          </aside>
        ) : null}
      </div>
    </section>
  );
}

function StepIndicator({
  step,
  stepIndex,
  stepOrder,
}: {
  step: Step;
  stepIndex: number;
  stepOrder: Step[];
}) {
  const labels: Record<Step, string> = {
    overview: 'Check in',
    'checked-in': 'Place',
    'in-chair': 'In chair',
    progress: 'Progress',
    'rebook-outcome': 'Rebook',
    payment: 'Payment',
    done: 'Done',
  };
  // Hide step indicator before in-chair flow begins.
  if (step === 'overview') return null;
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="floor-step-indicator">
      {stepOrder
        .filter((s) => s !== 'overview')
        .map((s, i, arr) => {
          const active = s === step;
          const past = stepOrder.indexOf(s) < stepIndex;
          return (
            <span key={s} className="flex items-center gap-1">
              <span
                className={clsx(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md font-inter text-[10px] uppercase tracking-widest',
                  active
                    ? 'bg-gold-bright/20 text-gold-bright border border-gold-bright/60'
                    : past
                      ? 'bg-mint/15 text-mint'
                      : 'bg-ink-deep text-cream-faint border border-gold-dim/20',
                )}
              >
                {past ? <CheckCircle2 size={10} /> : <CircleDot size={10} />}
                {labels[s]}
              </span>
              {i < arr.length - 1 ? <ArrowRight size={10} className="text-cream-faint" /> : null}
            </span>
          );
        })}
    </div>
  );
}

function CheckInPanel({
  customer,
  booking,
  artist,
  piece,
  clockIso,
  onCheckIn,
  onResendWaiver,
}: {
  customer: NonNullable<ReturnType<typeof useBrainStore.getState>['customers'][string]>;
  booking: NonNullable<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  artist: NonNullable<ReturnType<typeof useBrainStore.getState>['artists'][string]>;
  piece?: ReturnType<typeof useBrainStore.getState>['pieces'][string];
  clockIso: string;
  onCheckIn: () => void;
  onResendWaiver: () => void;
}) {
  const inspiration = useMemo(() => inspirationFor(customer), [customer]);
  const progressPhotos = piece?.progressPhotos ?? [];
  const waivers = useBrainStore((s) => s.waivers);
  const waiver = useMemo(
    () =>
      Object.values(waivers)
        .filter((w) => w.bookingId === booking.id)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0],
    [waivers, booking.id],
  );
  const [waiverOpen, setWaiverOpen] = useState(false);
  const wTone = waiverTone(waiver?.status ?? 'not-sent');
  const waiverSigned = waiver?.status === 'signed';
  // Cash/card steer at arrival - same engine the payment step reads, just
  // surfaced earlier so a manager can send the client to an ATM before the
  // chair, not only at checkout.
  const steer = useMemo(
    () => recommendPaymentMethod(booking.studioId, clockIso),
    [booking.studioId, clockIso],
  );
  const askCash = steer.method === 'cash';
  return (
    <div className="panel p-6 space-y-4" data-testid="check-in-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="label-small-caps text-gold-bright">Ready to check in</p>
        {askCash ? (
          <span className="flex items-center gap-2" data-testid="checkin-steer">
            <span className="pill-gold text-[10px]">Ask for cash</span>
            <button type="button" className="btn-quiet text-[10px]" data-testid="checkin-send-atm">
              Send to ATM
            </button>
          </span>
        ) : (
          <span className="pill-cream text-[10px]" data-testid="checkin-steer">
            Card is fine
          </span>
        )}
      </div>

      {/* Waiver + medical flags — safety context before the chair. */}
      <div
        className={clsx('rounded-md border p-3 flex items-center gap-3 flex-wrap', wTone.border, wTone.fill)}
        data-testid="check-in-waiver"
      >
        <span className={clsx('inline-flex items-center gap-1.5 text-xs font-inter uppercase tracking-widest', wTone.text)}>
          <ShieldCheck size={13} /> {wTone.label}
        </span>
        <CustomerFlagChips customer={customer} />
        {waiverSigned ? (
          <button
            type="button"
            onClick={() => setWaiverOpen(true)}
            className="btn-ghost text-[11px] ml-auto text-mint"
            data-testid="check-in-view-waiver"
          >
            View waiver
          </button>
        ) : null}
      </div>
      <WaiverModal open={waiverOpen} booking={booking} onClose={() => setWaiverOpen(false)} />
      <div className="font-serif text-cream-soft leading-relaxed">
        <p>
          {customer.firstName} is in for a {booking.sessionLength.replace('-', ' ')} with{' '}
          <span className="text-gold-bright">{artist.displayName}</span>. The brief is{' '}
          <span className="italic">{booking.designBrief}</span>.
        </p>
        {piece ? (
          <p className="mt-2">
            They&apos;re working through a {piece.title || piece.designType.replace('-', ' ')} on their{' '}
            {piece.placement}: completed {piece.hoursCompleted}h of {piece.estimatedTotalHours}h.
          </p>
        ) : null}
        <p className="mt-2">
          Check them in at the desk, then seat them in a chair when one&apos;s free — seating is what arms
          the booth recording and marks the chair live.
        </p>
      </div>
      {inspiration.length > 0 ? (
        <PhotoGallery
          photos={inspiration.map((p) => ({ url: p.url }))}
          label="Reference photos on file"
          size="medium"
          density="strip"
          testid="check-in-inspiration"
          maxVisible={6}
        />
      ) : null}
      {progressPhotos.length > 0 ? (
        <PhotoGallery
          photos={progressPhotos.map((p) => ({ url: p.url, caption: p.caption }))}
          label="Previous sessions"
          size="medium"
          density="strip"
          testid="check-in-progress"
          maxVisible={6}
        />
      ) : null}
      {waiverSigned ? (
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onCheckIn}
            className="btn-primary text-sm"
            data-testid="check-in-button"
          >
            <Radio size={14} /> Check in
          </button>
        </div>
      ) : (
        <div
          className="rounded-md border border-blood/50 bg-blood/10 p-4 space-y-3"
          data-testid="check-in-blocked"
        >
          <div className="flex items-center gap-2 text-blood">
            <AlertTriangle size={16} />
            <p className="label-small-caps text-blood">Waiver not signed</p>
          </div>
          <p className="font-serif text-cream-soft text-sm">
            {customer.firstName} can&apos;t be checked in until the medical waiver is signed. Sign it at
            the desk now, or resend the link to their phone.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setWaiverOpen(true)}
              className="btn-primary text-sm"
              data-testid="check-in-sign-at-desk"
            >
              <ShieldCheck size={14} /> Sign at desk now
            </button>
            <button
              type="button"
              onClick={onResendWaiver}
              className="btn-ghost text-sm"
              data-testid="check-in-resend-waiver"
            >
              <Send size={13} /> Resend waiver link
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Step 2 — the client is checked in and waiting. The chair follows the artist
 * (one artist per chair), so there's no booth to pick: it's a single "Place in
 * chair" action. Waiver + medical/allergy flags are surfaced one more time here
 * so they're seen right before the client goes to the chair.
 */
function SeatPanel({
  customer,
  booking,
  artist,
  onSeat,
  onBack,
}: {
  customer: NonNullable<ReturnType<typeof useBrainStore.getState>['customers'][string]>;
  booking: NonNullable<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  artist: NonNullable<ReturnType<typeof useBrainStore.getState>['artists'][string]>;
  onSeat: () => void;
  onBack: () => void;
}) {
  const waivers = useBrainStore((s) => s.waivers);
  const waiver = useMemo(
    () =>
      Object.values(waivers)
        .filter((w) => w.bookingId === booking.id)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0],
    [waivers, booking.id],
  );
  const wTone = waiverTone(waiver?.status ?? 'not-sent');

  return (
    <div className="panel p-6 space-y-4" data-testid="seat-panel">
      <p className="label-small-caps text-azure">Checked in · place them in the chair</p>

      {/* Safety context, one more time before the chair. */}
      <div
        className={clsx('rounded-md border p-3 flex items-center gap-3 flex-wrap', wTone.border, wTone.fill)}
        data-testid="seat-waiver"
      >
        <span className={clsx('inline-flex items-center gap-1.5 text-xs font-inter uppercase tracking-widest', wTone.text)}>
          <ShieldCheck size={13} /> {wTone.label}
        </span>
        <CustomerFlagChips customer={customer} />
      </div>

      <p className="font-serif text-cream-soft leading-relaxed">
        {customer.firstName} is checked in and ready for{' '}
        <span className="text-gold-bright">{artist.displayName}</span>. Placing them in the chair
        arms the booth recording and starts the in-chair timer.
      </p>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="btn-quiet text-sm"
          data-testid="seat-back"
        >
          <ChevronLeft size={12} /> Back
        </button>
        <button
          type="button"
          onClick={onSeat}
          className="btn-primary text-sm"
          data-testid="seat-button"
        >
          <Armchair size={14} /> Place in chair with {artist.firstName} · arm recording
        </button>
      </div>
    </div>
  );
}

function InChairPanel({
  booking,
  customer,
  artist,
  piece,
  onWrapUp,
  onBack,
}: {
  booking: NonNullable<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  customer: NonNullable<ReturnType<typeof useBrainStore.getState>['customers'][string]>;
  artist: NonNullable<ReturnType<typeof useBrainStore.getState>['artists'][string]>;
  piece?: ReturnType<typeof useBrainStore.getState>['pieces'][string];
  onWrapUp: () => void;
  onBack: () => void;
}) {
  const inspiration = useMemo(() => inspirationFor(customer), [customer]);
  const progressPhotos = piece?.progressPhotos ?? [];
  return (
    <div className="panel border-blood/40 bg-blood/5 p-6 space-y-4" data-testid="in-chair-panel">
      <div className="flex items-center gap-3">
        <Radio size={16} className="text-blood" />
        <p className="label-small-caps text-blood">Live session</p>
      </div>
      <p className="font-serif text-cream-soft leading-relaxed">
        {customer.firstName} is in chair with <span className="text-gold-bright">{artist.displayName}</span>.
        The brief on file: <span className="italic">{booking.designBrief}</span>.
      </p>
      {piece ? (
        <p className="font-serif text-cream-soft">
          They&apos;re working a {piece.title || piece.designType.replace('-', ' ')} on their {piece.placement}.{' '}
          <span className="text-gold-bright">
            Session {(piece.sessionsCompleted ?? 0) + 1} of {piece.estimatedTotalSessions ?? '?'}
          </span>{' '}
          ({piece.hoursCompleted}h of {piece.estimatedTotalHours}h done coming in).
        </p>
      ) : null}
      {inspiration.length > 0 ? (
        <PhotoGallery
          photos={inspiration.map((p) => ({ url: p.url }))}
          label="Reference photos on file"
          size="medium"
          density="strip"
          testid="in-chair-inspiration"
          maxVisible={6}
        />
      ) : null}
      {progressPhotos.length > 0 ? (
        <PhotoGallery
          photos={progressPhotos.map((p) => ({ url: p.url, caption: p.caption }))}
          label="Work so far"
          size="medium"
          density="strip"
          testid="in-chair-progress"
          maxVisible={6}
        />
      ) : null}
      <p className="font-inter text-[11px] text-cream-soft uppercase tracking-widest">
        Recording. Capture observations as you walk by, drop them in at check-out.
      </p>
      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="btn-quiet text-sm"
          data-testid="in-chair-back"
        >
          <ChevronLeft size={12} /> Back
        </button>
        <button
          type="button"
          onClick={onWrapUp}
          className="btn-primary text-sm"
          data-testid="wrap-up-button"
        >
          <ClipboardList size={14} /> Wrap up · start check-out
        </button>
      </div>
    </div>
  );
}

/**
 * Notes — ambient panel. Collapsed by default; click the header to expand.
 * Edits land directly on parent state so they persist through every step.
 */
function NotesAmbientPanel({
  expanded,
  setExpanded,
  sessionNote,
  setSessionNote,
  futurePhrase,
  setFuturePhrase,
}: {
  expanded: boolean;
  setExpanded: (b: boolean) => void;
  sessionNote: string;
  setSessionNote: (s: string) => void;
  futurePhrase: string;
  setFuturePhrase: (s: string) => void;
}) {
  const captured = (sessionNote.trim() ? 1 : 0) + (futurePhrase.trim() ? 1 : 0);
  return (
    <div className="panel border-gold-dim/30" data-testid="ambient-notes">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="ambient-notes-toggle"
      >
        <span className="flex items-center gap-2 label-small-caps">
          <ClipboardList size={12} className="text-gold-bright" />
          Notes
          <span className="font-mono text-[10px] text-cream-faint normal-case tracking-widest">
            {captured} captured
          </span>
        </span>
        {expanded ? (
          <ChevronUp size={14} className="text-gold-dim" />
        ) : (
          <ChevronDown size={14} className="text-gold-dim" />
        )}
      </button>
      {expanded ? (
        <div className="px-4 pb-4 space-y-3" data-testid="ambient-notes-content">
          <p className="font-serif text-cream-soft text-[12px]">
            Lands on the customer record so the next call has context. Editable at any time.
          </p>
          <label className="block">
            <span className="label-small-caps flex items-center gap-1">
              Rapport note
              <InfoTip label="What to ask">
                <p className="font-semibold mb-1">A few things worth asking (service, not a sale):</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>What are they planning for the piece next?</li>
                  <li>Any travel or holidays coming up?</li>
                  <li>How did they find the studio?</li>
                  <li>What do they do for work?</li>
                  <li>When does their pay cycle land - any budget timing?</li>
                  <li>Anything healing-related from last time?</li>
                </ul>
              </InfoTip>
            </span>
            <textarea
              value={sessionNote}
              onChange={(e) => setSessionNote(e.target.value)}
              rows={2}
              placeholder="What did you learn about them today?"
              className="mt-1.5 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-serif focus:outline-none focus:border-gold-bright"
              data-testid="notes-rapport"
            />
          </label>
          <label className="block">
            <span className="label-small-caps">Future-readiness phrase</span>
            <input
              value={futurePhrase}
              onChange={(e) => setFuturePhrase(e.target.value)}
              placeholder='e.g. "after my holiday in July"'
              className="mt-1.5 w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
              data-testid="notes-future"
            />
          </label>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Snaps — ambient panel. Same shape as the old wizard step, minus the
 * continue/back navigation. Operator can capture progress photos any time.
 */
function SnapsAmbientPanel({
  expanded,
  setExpanded,
  piece,
  booking,
  snappedPhotos,
  setSnappedPhotos,
}: {
  expanded: boolean;
  setExpanded: (b: boolean) => void;
  piece?: ReturnType<typeof useBrainStore.getState>['pieces'][string];
  booking: NonNullable<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  snappedPhotos: string[];
  setSnappedPhotos: (p: string[]) => void;
}) {
  const candidates = useMemo(() => {
    const designType = piece?.designType ?? 'sleeve';
    return pickReferences(designType, 6, `${booking.id}-snap`);
  }, [piece?.designType, booking.id]);
  const existingProgress = piece?.progressPhotos ?? [];

  const toggle = (url: string) => {
    setSnappedPhotos(
      snappedPhotos.includes(url)
        ? snappedPhotos.filter((u) => u !== url)
        : [...snappedPhotos, url],
    );
  };

  return (
    <div className="panel border-gold-dim/30" data-testid="ambient-snaps">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="ambient-snaps-toggle"
      >
        <span className="flex items-center gap-2 label-small-caps">
          <Camera size={12} className="text-gold-bright" />
          Photos
          <span className="font-mono text-[10px] text-cream-faint normal-case tracking-widest">
            {snappedPhotos.length} this session · {existingProgress.length} on file
          </span>
        </span>
        {expanded ? (
          <ChevronUp size={14} className="text-gold-dim" />
        ) : (
          <ChevronDown size={14} className="text-gold-dim" />
        )}
      </button>
      {expanded ? (
        <div
          className="px-4 pb-4 space-y-3"
          data-testid="snap-photos-panel"
        >
          {existingProgress.length > 0 ? (
            <PhotoGallery
              photos={existingProgress.map((p) => ({ url: p.url, caption: p.caption }))}
              label="Already on file"
              size="thumb"
              density="strip"
              testid="snap-existing"
              maxVisible={6}
            />
          ) : null}
          <div>
            <p className="label-small-caps text-gold-bright mb-1.5">Tap to capture</p>
            <div className="flex flex-wrap gap-1.5" data-testid="snap-candidates">
              {candidates.map((url, i) => {
                const selected = snappedPhotos.includes(url);
                return (
                  <button
                    type="button"
                    key={`${url}-${i}`}
                    onClick={() => toggle(url)}
                    data-testid={`snap-candidate-${i}`}
                    className={clsx(
                      'relative h-16 w-16 overflow-hidden rounded-md border transition',
                      selected
                        ? 'border-mint shadow-gold-glow ring-2 ring-mint'
                        : 'border-gold-dim/30 hover:border-gold-bright/70',
                    )}
                  >
                    <img src={url} alt="capture candidate" loading="lazy" className="h-full w-full object-cover" />
                    {selected ? (
                      <span className="absolute top-1 right-1 bg-mint text-ink-black rounded-full p-0.5">
                        <CheckCircle2 size={10} />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ProgressPanel({
  piece,
  hoursToAdd,
  setHoursToAdd,
  markComplete,
  setMarkComplete,
  defaultDurationHours,
  snappedPhotosCount,
  existingProgressCount,
  onJumpToSnaps,
  onContinue,
  onBack,
}: {
  piece?: ReturnType<typeof useBrainStore.getState>['pieces'][string];
  hoursToAdd: number;
  setHoursToAdd: (n: number) => void;
  markComplete: boolean;
  setMarkComplete: (b: boolean) => void;
  defaultDurationHours: number;
  snappedPhotosCount: number;
  existingProgressCount: number;
  onJumpToSnaps: () => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  // Soft nudge: if nothing has been snapped this session and the piece has
  // no previous photos either, the operator probably forgot. Don't block —
  // give them a one-click route to the snaps panel.
  const showSnapNudge = snappedPhotosCount === 0;
  return (
    <div className="panel p-6 space-y-4" data-testid="progress-panel">
      <p className="label-small-caps text-gold-bright">Update piece progress</p>
      {showSnapNudge ? (
        <div
          className="rounded-md border border-gold-bright/40 bg-gold/5 px-3 py-2 flex items-start gap-2"
          data-testid="snap-nudge"
        >
          <Camera size={14} className="text-gold-bright shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-serif text-cream text-sm leading-snug">
              No photos captured this session.{' '}
              {existingProgressCount > 0
                ? "Worth snapping one so the next session sees today's work."
                : 'Worth snapping one for next time.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onJumpToSnaps}
            className="btn-quiet text-[11px] shrink-0"
            data-testid="snap-nudge-open"
          >
            <Camera size={11} /> Snap now
          </button>
        </div>
      ) : null}
      {piece ? (
        <>
          <p className="font-serif text-cream-soft">
            Coming in they were at <span className="text-gold-bright font-inter">{piece.hoursCompleted}h of {piece.estimatedTotalHours}h</span>{' '}
            on the {piece.title || piece.designType.replace('-', ' ')} on their {piece.placement}.
          </p>
          <label className="block">
            <span className="label-small-caps">Hours done in this session</span>
            <input
              type="number"
              min={0}
              max={12}
              step={0.5}
              value={hoursToAdd}
              onChange={(e) => setHoursToAdd(parseFloat(e.target.value) || 0)}
              className="mt-2 w-32 bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-inter"
              data-testid="progress-hours"
            />
            <span className="ml-2 font-inter text-[10px] text-cream-soft uppercase tracking-widest">
              of {defaultDurationHours.toFixed(1)}h scheduled
            </span>
          </label>
          <p className="font-serif text-cream-soft">
            New total after this session: <span className="text-gold-bright">{Math.min(piece.estimatedTotalHours, piece.hoursCompleted + hoursToAdd).toFixed(1)}h</span>{' '}
            of {piece.estimatedTotalHours}h.
          </p>
          <label className="flex items-center gap-2 text-sm text-cream cursor-pointer">
            <input
              type="checkbox"
              checked={markComplete}
              onChange={(e) => setMarkComplete(e.target.checked)}
              className="accent-gold-bright"
              data-testid="progress-mark-complete"
            />
            Piece is now complete (no more sessions needed)
          </label>
        </>
      ) : (
        <p className="font-serif text-cream-soft">
          This booking isn&apos;t linked to a multi-session piece. Skip ahead.
        </p>
      )}
      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="btn-quiet text-sm"
          data-testid="progress-back"
        >
          <ChevronLeft size={12} /> Back
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="btn-primary text-sm"
          data-testid="progress-continue"
        >
          Continue <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function PaymentPanel({
  booking,
  customer,
  piece,
  artistName,
  rebookSummary,
  clockIso,
  sessionPrice,
  depositPaidAud,
  depositRef,
  rolloverDeposit,
  depositAppliedEffective,
  balanceDueAud,
  collectNowAud,
  merchCatalogue,
  paymentMethod,
  setPaymentMethod,
  sale,
  setSale,
  paymentDone,
  onCharge,
  onContinue,
  onBack,
}: {
  booking: NonNullable<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  customer: NonNullable<ReturnType<typeof useBrainStore.getState>['customers'][string]>;
  piece?: ReturnType<typeof useBrainStore.getState>['pieces'][string];
  artistName: string;
  rebookSummary: { title: string; detail?: string };
  clockIso: string;
  sessionPrice: number;
  depositPaidAud: number;
  depositRef?: string;
  rolloverDeposit: boolean;
  depositAppliedEffective: number;
  balanceDueAud: number;
  collectNowAud: number;
  merchCatalogue: MerchItem[];
  paymentMethod: 'card' | 'cash' | 'mixed';
  setPaymentMethod: (m: 'card' | 'cash' | 'mixed') => void;
  sale: SaleState;
  setSale: (updater: (prev: SaleState) => SaleState) => void;
  paymentDone: boolean;
  onCharge: () => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  // Steering + reference read the live day, with THIS not-yet-committed sale
  // projected on top (D12) - so choosing cash/card, or the collectable moving
  // (merch, adjustment), steers the banner live without double-counting once
  // the sale actually lands on the committed ledger.
  const steer = useMemo(
    () => projectedRecommendation(booking.studioId, clockIso, { method: paymentMethod, collectNowAud }),
    [booking.studioId, clockIso, paymentMethod, collectNowAud],
  );
  // Baseline (un-projected) steer - "was this client flagged for cash" -
  // independent of whatever method is currently selected in the picker below.
  // Used ONLY to trigger the card guard (D5b); using the projected `steer`
  // here would be circular, since choosing card is exactly what suppresses
  // the projected cash that would otherwise flip it back to "ask for cash".
  const baselineSteer = useMemo(
    () => recommendPaymentMethod(booking.studioId, clockIso),
    [booking.studioId, clockIso],
  );
  const reference = useMemo(
    () => makeSaleReference(booking.studioId, booking.id, clockIso),
    [booking.studioId, booking.id, clockIso],
  );
  const askCash = steer.method === 'cash';
  const afterCutoff = Number(clockIso.slice(11, 13)) >= SETTLEMENT_CUTOFF_HOUR;
  const cashLike = paymentMethod === 'cash' || paymentMethod === 'mixed';
  const changeDue = Math.max(0, sale.cashTendered - collectNowAud);
  const adjustmentAud = sale.adjustment?.amountAud ?? 0;

  // Card guard (D5b) - the client was flagged for cash, but the manager has
  // card selected at the payment step. Switching to cash always re-arms the
  // guard for next time; only an explicit override silences it while card
  // stays selected.
  const [cardOverride, setCardOverride] = useState(false);
  const cardGuardActive = baselineSteer.method === 'cash' && paymentMethod === 'card' && !cardOverride;
  const selectPaymentMethod = (m: 'card' | 'cash' | 'mixed') => {
    setPaymentMethod(m);
    if (m === 'cash') setCardOverride(false);
  };

  const [merchQuery, setMerchQuery] = useState('');
  const filteredCatalogue = merchCatalogue.filter((m) =>
    m.name.toLowerCase().includes(merchQuery.toLowerCase()),
  );

  const addMerch = (item: MerchItem) =>
    setSale((prev) => ({
      ...prev,
      merchLines: [...prev.merchLines, { itemId: item.id, name: item.name, priceAud: item.priceAud }],
    }));
  const removeMerch = (idx: number) =>
    setSale((prev) => ({ ...prev, merchLines: prev.merchLines.filter((_, i) => i !== idx) }));

  // Reasoned +/- charge adjustment (D3). Local sign/amount/reason drive the
  // control; seeded from any adjustment already on the sale (draft restore) so
  // the fields survive a remount. The derived signed adjustment is written back
  // onto `sale` the same way the merch lines are - a valid adjustment needs BOTH
  // a non-zero amount and a non-empty reason, otherwise it does not apply.
  const [adjustSign, setAdjustSign] = useState<1 | -1>(() =>
    sale.adjustment && sale.adjustment.amountAud > 0 ? 1 : -1,
  );
  const [adjustAmount, setAdjustAmount] = useState<number>(() =>
    sale.adjustment ? Math.abs(sale.adjustment.amountAud) : 0,
  );
  const [adjustReason, setAdjustReason] = useState<string>(() => sale.adjustment?.reason ?? '');
  useEffect(() => {
    const signedAud = adjustSign * adjustAmount;
    const trimmedReason = adjustReason.trim();
    const next = signedAud !== 0 && trimmedReason ? { amountAud: signedAud, reason: trimmedReason } : null;
    setSale((prev) => {
      const unchanged =
        prev.adjustment === next ||
        (prev.adjustment !== null &&
          next !== null &&
          prev.adjustment.amountAud === next.amountAud &&
          prev.adjustment.reason === next.reason);
      return unchanged ? prev : { ...prev, adjustment: next };
    });
  }, [adjustSign, adjustAmount, adjustReason, setSale]);

  return (
    <div className="space-y-5" data-testid="payment-panel">
      {/* Summary strip (WS2): customer · appointment · rebook outcome. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="payment-summary">
        <div className="panel-dark p-3">
          <p className="label-small-caps text-gold-bright">Customer</p>
          <p className="font-serif text-cream mt-1">{customerName(customer)}</p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">{customer.phone}</p>
        </div>
        <div className="panel-dark p-3">
          <p className="label-small-caps text-gold-bright">Appointment</p>
          <p className="font-serif text-cream mt-1 capitalize">
            {piece ? piece.title || piece.designType.replace('-', ' ') : booking.sessionLength.replace('-', ' ')}
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
            with {artistName} · {booking.sessionLength.replace('-', ' ')}
            {piece?.estimatedTotalSessions
              ? ` · session ${(piece.sessionsCompleted ?? 0) + 1} of ${piece.estimatedTotalSessions}`
              : ''}
          </p>
        </div>
        <div className="panel-dark p-3 border-mint/30">
          <p className="label-small-caps text-gold-bright">Rebook outcome</p>
          <p className="font-serif text-cream mt-1">{rebookSummary.title}</p>
          {rebookSummary.detail ? (
            <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">{rebookSummary.detail}</p>
          ) : null}
        </div>
      </div>

      {/* Cash-vs-card steering — the headline of the make-sale. */}
      <div
        className={clsx(
          'rounded-lg border p-4 flex items-center gap-3',
          askCash ? 'border-blood/60 bg-blood/10' : 'border-mint/50 bg-mint/10',
        )}
        data-testid="payment-steer"
      >
        <Banknote size={20} className={clsx('shrink-0', askCash ? 'text-blood' : 'text-mint')} />
        <div className="flex-1">
          <p className={clsx('font-cinzel text-lg', askCash ? 'text-blood' : 'text-mint')}>
            {askCash ? 'Ask for cash' : 'Card is fine'}
          </p>
          <p className="font-inter text-[12px] text-cream-soft mt-0.5" data-testid="payment-steer-why">
            {steer.reasoning}
          </p>
        </div>
      </div>

      <SteeringExplainer
        studioId={booking.studioId}
        dayIso={clockIso}
        steer={steer}
        baseSteer={baselineSteer}
        showCoverTable
      />

      {/* Wide working area (WS2): inputs left; reference + invoice + customer
          context right, so the widescreen space is used and the invoice sits
          under the reference, in view. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4 items-start">
        {/* LEFT — tally the sale, then choose how to pay. */}
        <div className="space-y-4">
          {/* Deposit pot (decision from rebook) + partial use. */}
          {depositPaidAud > 0 ? (
            <div className="panel p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-md border border-mint/40 bg-mint/5 p-3">
                <p className="label-small-caps text-mint">Deposit pot · available</p>
                <p className="font-inter text-sm text-cream mt-1">
                  {aud(depositPaidAud)} on file
                  {depositRef ? <span className="font-mono text-[11px] text-cream-faint"> · {depositRef}</span> : null}
                </p>
                <span className="pill-mint mt-2 inline-flex text-[10px]">
                  {rolloverDeposit ? 'Rolling to next session' : 'Applied to today'}
                </span>
              </div>
              <div className="rounded-md border border-gold-dim/30 p-3">
                <p className="label-small-caps">Partial deposit use</p>
                <div className="flex gap-2 mt-2 items-center">
                  <input
                    type="number"
                    min={0}
                    max={depositPaidAud}
                    value={depositAppliedEffective}
                    onChange={(e) =>
                      setSale((prev) => ({
                        ...prev,
                        depositApplied: Math.min(depositPaidAud, Math.max(0, parseFloat(e.target.value) || 0)),
                      }))
                    }
                    className="w-24 bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1.5 text-sm text-cream font-mono"
                    data-testid="payment-deposit-applied"
                  />
                  <input
                    type="text"
                    value={sale.depositReason}
                    onChange={(e) => setSale((prev) => ({ ...prev, depositReason: e.target.value }))}
                    placeholder="Reason (logged)…"
                    className="flex-1 bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1.5 text-sm text-cream"
                  />
                </div>
              </div>
            </div>
          ) : null}

          {/* Merch / aftercare picker. */}
          <div className="panel p-4">
            <p className="label-small-caps mb-2">
              Merch &amp; aftercare <span className="text-cream-faint">· no commission</span>
            </p>
            <div className="flex items-center gap-2 bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1.5">
              <Search size={13} className="text-cream-faint" />
              <input
                value={merchQuery}
                onChange={(e) => setMerchQuery(e.target.value)}
                placeholder="Search items…"
                className="flex-1 bg-transparent text-sm text-cream focus:outline-none"
                data-testid="merch-search"
              />
            </div>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-gold-dim/20 divide-y divide-gold-dim/10">
              {filteredCatalogue.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => addMerch(item)}
                  data-testid={`merch-add-${item.id}`}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gold-bright/5"
                >
                  <span className="text-sm text-cream">{item.name}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-sm text-cream-soft">{aud(item.priceAud)}</span>
                    <Plus size={13} className="text-gold-bright" />
                  </span>
                </button>
              ))}
              {filteredCatalogue.length === 0 ? (
                <p className="px-3 py-2 text-[12px] text-cream-faint">No items.</p>
              ) : null}
            </div>
            {sale.merchLines.length > 0 ? (
              <ul className="mt-2 space-y-1" data-testid="merch-selected">
                {sale.merchLines.map((l, i) => (
                  <li key={`${l.itemId}-${i}`} className="flex items-center justify-between text-sm text-cream">
                    <span>{l.name}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono">{aud(l.priceAud)}</span>
                      <button
                        type="button"
                        onClick={() => removeMerch(i)}
                        className="text-cream-faint hover:text-blood"
                        aria-label={`Remove ${l.name}`}
                      >
                        <X size={13} />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {/* Reasoned +/- charge adjustment (D3) — signed, reasoned line on top of
              the balance. Needs BOTH a non-zero amount and a reason to apply. */}
          <div className="panel p-4">
            <p className="label-small-caps mb-2">
              Adjustment <span className="text-cream-faint">· reasoned only</span>
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex rounded-md border border-gold-dim/30 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setAdjustSign(-1)}
                  data-testid="adjust-reduce"
                  className={clsx(
                    'px-3 py-1.5 text-xs uppercase tracking-widest font-inter transition',
                    adjustSign === -1 ? 'bg-blood/20 text-blood' : 'text-cream-soft hover:bg-blood/5',
                  )}
                >
                  Reduce
                </button>
                <button
                  type="button"
                  onClick={() => setAdjustSign(1)}
                  data-testid="adjust-add"
                  className={clsx(
                    'px-3 py-1.5 text-xs uppercase tracking-widest font-inter transition border-l border-gold-dim/30',
                    adjustSign === 1 ? 'bg-mint/20 text-mint' : 'text-cream-soft hover:bg-mint/5',
                  )}
                >
                  Add
                </button>
              </div>
              <input
                type="number"
                min={0}
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                className="w-24 bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1.5 text-sm text-cream font-mono"
                data-testid="adjust-amount"
              />
              <input
                type="text"
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="Reason (required)"
                className="flex-1 min-w-[10rem] bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1.5 text-sm text-cream"
                data-testid="adjust-reason"
              />
            </div>
            {sale.adjustment ? (
              <p className="mt-2 font-mono text-sm text-cream" data-testid="adjust-line">
                {sale.adjustment.amountAud < 0 ? '-' : '+'}
                {aud(Math.abs(sale.adjustment.amountAud))} · {sale.adjustment.reason}
              </p>
            ) : null}
          </div>

          {/* Payment method + card guard + cash tendered. Chosen last: settle the
              amount above, then how it is paid. */}
          <div className="panel p-4">
            <p className="label-small-caps text-gold-bright mb-2">Payment method</p>
            <div className="flex gap-2 flex-wrap">
              {(['card', 'cash', 'mixed'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => selectPaymentMethod(m)}
                  className={clsx(
                    'px-3 py-1.5 rounded-md border text-xs uppercase tracking-widest font-inter transition',
                    paymentMethod === m
                      ? 'border-gold-bright bg-gold/10 text-gold-bright'
                      : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
                  )}
                  data-testid={`payment-method-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>

            {/* Card guard (D5b) — flagged for cash but card selected. */}
            {cardGuardActive ? (
              <div className="mt-3 rounded-md border border-blood/50 bg-blood/10 p-4 space-y-3" data-testid="card-guard">
                <div className="flex items-center gap-2 text-blood">
                  <AlertTriangle size={16} />
                  <p className="label-small-caps text-blood">Flagged for cash</p>
                </div>
                <p className="font-serif text-cream-soft text-sm">
                  This client was flagged for cash. Taking card leaves a {aud(baselineSteer.shortfallAud)} gap. Take
                  card anyway?
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => selectPaymentMethod('cash')}
                    className="btn-primary text-sm"
                    data-testid="card-guard-switch"
                  >
                    <Banknote size={14} /> Switch to cash
                  </button>
                  <button
                    type="button"
                    onClick={() => setCardOverride(true)}
                    className="btn-quiet text-sm"
                    data-testid="card-guard-override"
                  >
                    Override, take card
                  </button>
                </div>
              </div>
            ) : null}

            {/* Cash tendered → change (cash / mixed). */}
            {cashLike ? (
              <div className="mt-3 panel-dark p-3" data-testid="payment-cash-row">
                <p className="label-small-caps text-cream-faint">Cash tendered → change</p>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="number"
                    min={0}
                    value={sale.cashTendered}
                    onChange={(e) => setSale((prev) => ({ ...prev, cashTendered: Math.max(0, parseFloat(e.target.value) || 0) }))}
                    className="w-28 bg-ink-deep border border-gold-dim/40 rounded-md px-2 py-1.5 text-sm text-cream font-mono"
                    data-testid="payment-cash-tendered"
                  />
                  <span className="font-inter text-[11px] text-cream-faint uppercase tracking-widest">tendered</span>
                  <ArrowRight size={12} className="text-cream-faint" />
                  <span className="font-mono text-blood">{aud(changeDue)}</span>
                  <span className="font-inter text-[11px] text-cream-faint uppercase tracking-widest">change</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* RIGHT rail — at a glance, reference, invoice, then customer context. */}
        <div className="space-y-4">
          {/* At a glance (was the balance grid). */}
          <div className="panel p-4">
            <p className="label-small-caps text-gold-bright mb-2">At a glance</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="panel-dark p-3">
                <p className="label-small-caps text-cream-faint">Day rate</p>
                <p className="font-mono text-lg text-cream mt-1">{aud(sessionPrice)}</p>
              </div>
              <div className="panel-dark p-3">
                <p className="label-small-caps text-cream-faint">Deposit</p>
                <p className="font-mono text-lg text-cream mt-1">{aud(depositAppliedEffective)}</p>
              </div>
              <div className="panel-dark p-3">
                <p className="label-small-caps text-cream-faint">Balance due</p>
                <p className="font-mono text-lg text-gold-bright mt-1" data-testid="payment-balance-due">{aud(balanceDueAud)}</p>
              </div>
            </div>
          </div>

          {/* Reference / reconciliation. */}
          <div className="panel p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="label-small-caps text-gold-bright">Make-sale</p>
              <div className="text-right">
                <p className="label-small-caps text-cream-faint">Reference · auto</p>
                <p className="font-mono text-xs text-cream-soft" data-testid="payment-reference">{reference}</p>
              </div>
            </div>
            <div className="rounded-md border border-mint/30 bg-mint/5 p-3 flex gap-2">
              <Info size={14} className="text-mint shrink-0 mt-0.5" />
              <p className="font-serif text-[12px] text-cream-soft">
                Reference <span className="font-mono">{reference}</span> is stamped on the sale and echoed at
                settlement — the bank lump auto-splits. Client + artist names stay on the record; no docket to write.
              </p>
            </div>
          </div>

          {/* Invoice — the single canonical total, with the charge / done actions
              right beside it (WS2: total shown once, in reach). */}
          <div className="panel-dark p-4" data-testid="payment-invoice">
            <p className="label-small-caps text-gold-bright mb-2">Invoice</p>
            <div className="divide-y divide-gold-dim/15">
              <InvoiceLine desc={`Session — ${booking.sessionLength.replace('-', ' ')}`} amount={aud(sessionPrice)} />
              {sale.merchLines.map((l, i) => (
                <InvoiceLine key={`${l.itemId}-${i}`} desc={`Merch — ${l.name}`} amount={aud(l.priceAud)} />
              ))}
              {sale.adjustment && sale.adjustment.amountAud !== 0 ? (
                <InvoiceLine
                  desc={`Adjustment${sale.adjustment.reason ? ` — ${sale.adjustment.reason}` : ''}`}
                  amount={`${adjustmentAud < 0 ? '−' : '+'}${aud(Math.abs(adjustmentAud))}`}
                  credit={adjustmentAud < 0}
                />
              ) : null}
              {sessionPrice - balanceDueAud > 0 ? (
                <InvoiceLine
                  desc={`Deposit applied${depositRef ? ` · ${depositRef}` : ''}`}
                  amount={`−${aud(sessionPrice - balanceDueAud)}`}
                  credit
                />
              ) : null}
            </div>
            <div className="flex items-center justify-between pt-3 mt-2 border-t border-gold-dim/40">
              <span className="font-serif text-cream">Total to collect</span>
              <span className="font-cinzel text-3xl text-gold-bright" data-testid="invoice-total">{aud(collectNowAud)}</span>
            </div>
            {cashLike ? (
              <p className="font-inter text-[11px] text-cream-soft mt-1">
                {aud(Math.min(sale.cashTendered, collectNowAud))} to till
                {changeDue > 0 ? ` · ${aud(changeDue)} change` : ''}
              </p>
            ) : null}
            {afterCutoff ? (
              <span className="pill-cream inline-flex text-[10px] mt-2" data-testid="payment-cutoff-chip">
                After 17:00 · tomorrow&apos;s lump
              </span>
            ) : null}
            <div className="flex items-center gap-2 mt-4">
              {paymentDone ? (
                <span
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-mint/20 text-mint text-sm font-inter uppercase tracking-widest"
                  data-testid="payment-done-pill"
                >
                  <CheckCircle2 size={14} /> Paid
                </span>
              ) : (
                <button type="button" onClick={onCharge} className="btn-primary text-sm flex-1" data-testid="payment-charge">
                  {paymentMethod === 'card' ? <CreditCard size={14} /> : <Banknote size={14} />} Charge
                </button>
              )}
              <button
                type="button"
                onClick={onContinue}
                disabled={!paymentDone || cardGuardActive}
                className="btn-primary text-sm flex-1"
                data-testid="payment-continue"
              >
                <Sparkles size={14} /> Done
              </button>
            </div>
          </div>

          {/* Customer context — kept from the sidebar so nothing is lost (WS2). */}
          <CustomerSidebar customer={customer} piece={piece} />
        </div>
      </div>

      <div className="flex justify-start">
        <button type="button" onClick={onBack} className="btn-quiet text-sm">
          <ChevronLeft size={12} /> Back
        </button>
      </div>
    </div>
  );
}

/**
 * Shown at the rebook step when the current persona can't record the outcome
 * (artist/apprentice/bookings-agent). Recording the outcome + decline reason +
 * payment is the studio manager's make-sale job; the artist's contribution is
 * the lightweight in-chair read they leave from their own dashboard.
 */
function RebookOutcomeBlockedPanel({
  artistName,
  onBack,
}: {
  artistName: string;
  onBack: () => void;
}) {
  return (
    <div className="panel p-6 space-y-4" data-testid="rebook-outcome-blocked">
      <div className="flex items-center gap-2 text-gold-bright">
        <AlertTriangle size={16} />
        <p className="label-small-caps text-gold-bright">Manager records the rebook</p>
      </div>
      <p className="font-serif text-cream-soft leading-relaxed">
        Recording the rebook outcome — booking the next session, logging a decline reason, or
        deferring to sales — is the studio manager&apos;s job at the make-sale. {artistName} can flag
        their read from the in-chair screen, and it&apos;ll pre-fill this panel for the manager.
      </p>
      <button type="button" onClick={onBack} className="btn-quiet text-sm" data-testid="rebook-blocked-back">
        <ChevronLeft size={12} /> Back
      </button>
    </div>
  );
}

function DonePanel({
  customerName,
  amount,
  rolled,
  onBack,
}: {
  customerName: string;
  amount: number;
  rolled: boolean;
  onBack: () => void;
}) {
  return (
    <div className="panel p-6 space-y-4 text-center" data-testid="done-panel">
      <CheckCircle2 size={48} className="text-mint mx-auto" />
      <h2 className="heading-display text-2xl text-cream">Wrapped</h2>
      <p className="font-serif text-cream-soft">
        {customerName} paid {aud(amount)}.{' '}
        {rolled
          ? 'Their deposit rolled forward and the next session is locked in.'
          : 'No rebook on the books · flag for callback later this week.'}
      </p>
      <button type="button" onClick={onBack} className="btn-primary text-sm">
        Back to floor
      </button>
    </div>
  );
}

function CustomerSidebar({
  customer,
  piece,
}: {
  customer: NonNullable<ReturnType<typeof useBrainStore.getState>['customers'][string]>;
  piece?: ReturnType<typeof useBrainStore.getState>['pieces'][string];
}) {
  const inspiration = useMemo(() => inspirationFor(customer), [customer]);
  return (
    <div className="space-y-4" data-testid="floor-customer-sidebar">
      {inspiration.length > 0 ? (
        <div className="panel-dark p-4">
          <PhotoGallery
            photos={inspiration.map((p) => ({ url: p.url }))}
            label="On their record"
            size="thumb"
            density="compact"
            testid="sidebar-inspiration"
            maxVisible={6}
          />
        </div>
      ) : null}

      <div className="panel-dark p-4">
        <p className="label-small-caps">Profile</p>
        <p className="font-inter text-base text-cream mt-2">{customer.firstName} {customer.lastName}</p>
        <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
          {customer.phone}
        </p>
        <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">
          {customer.suburb} · {customer.distanceToNearestStudioKm.toFixed(1)} km away
        </p>
        <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-2">
          Lifetime spend: {aud(customer.lifetimeSpendAud)}
        </p>
      </div>

      {piece ? (
        <div className="panel-dark p-4">
          <p className="label-small-caps">Piece in progress</p>
          <p className="font-inter text-sm text-cream mt-2 capitalize">
            {piece.title || piece.designType.replace('-', ' ')} · {piece.placement}
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
            {piece.hoursCompleted}h of {piece.estimatedTotalHours}h done
          </p>
          {piece.estimatedTotalSessions ? (
            <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-0.5">
              {piece.sessionsCompleted ?? 0} of {piece.estimatedTotalSessions} sessions
            </p>
          ) : null}
        </div>
      ) : null}

      {customer.rapportNotes.length > 0 ? (
        <div className="panel-dark p-4">
          <p className="label-small-caps flex items-center gap-1.5">
            <Sparkles size={11} /> What we know
          </p>
          <ul className="mt-2 space-y-2">
            {customer.rapportNotes.slice(-4).map((n) => (
              <li key={n.id} className="font-serif text-sm text-cream leading-relaxed">
                &quot;{n.note}&quot;
                <p className="font-inter text-[9px] text-cream-faint uppercase tracking-widest mt-0.5">
                  {n.author}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {customer.designIntent ? (
        <div className="panel-dark p-4">
          <p className="label-small-caps flex items-center gap-1.5">
            <Wallet size={11} /> Design intent
          </p>
          <p className="font-serif text-sm text-cream mt-2 capitalize">
            {customer.designIntent.type.replace('-', ' ')} on {customer.designIntent.placement}
          </p>
          {customer.designIntent.notes ? (
            <p className="font-serif text-[12px] text-cream-soft mt-1">
              {customer.designIntent.notes}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** One line on the checkout invoice (K4). Credits (deposit, reduction) read green. */
function InvoiceLine({ desc, amount, credit }: { desc: string; amount: string; credit?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
      <span className="text-cream">{desc}</span>
      <span className={clsx('font-mono tabular-nums whitespace-nowrap', credit ? 'text-mint' : 'text-cream-soft')}>
        {amount}
      </span>
    </div>
  );
}
